import { differencewith, isequal, topairs } from '@quromedical/utils';
import type { AuditEvent, BundleEntryRequest, FhirResource } from 'fhir/r4';

import { type AuditEventCode, system } from '../coding';
import type { BundleEntry, Editor, MixedBundle, ResourceType } from '../types';
import {
  createBundle,
  createBundleEntry,
  mapResourceToBundleEntry,
} from './bundle';
import {
  attachBundleFullUrl,
  createReference,
  createReferenceFromResource,
  createUrlReference,
  generateBundleReferenceId,
} from './id';
import type { CreateReference } from './reference';

export interface CreateAuditEvent {
  /**
   * Code for the event being audited
   */
  event: AuditEventCode;
  /**
   * A `Date` or `ISOString` representation of one
   */
  when: Date | string;
  editor: Editor;
  toWhatResource: ResourceType;
  /**
   * The resource ID or an internal bundle reference. If using an internal `urn` reference ensure
   * `isFullUrl` is set to true
   */
  toWhatId: string;
  /**
   * If this is set to true then the `toWhatResource` will not be appended to the reference.
   * This is used for internal-bundle references when auditing a new resource
   */
  isFullUrl?: boolean;
  /**
   * The list of changed properties in the resource. This is used to track changes in the resource.
   */
  changedProperties?: string[];
  /**
   * The previous version of the resource before the change. This enables change tracking
   * and audit investigations by allowing manual diff between versions.
   */
  previousVersion?: FhirResource;
  /**
   * The created version of the resource. This enables tracking of what was created
   * for create operations, providing a complete audit trail.
   */
  createdVersion?: FhirResource;
}

/**
 * Get the top-level differences between two objects only shallowly comparing properties.
 * This function is useful for auditing purposes where you want to track changes in the top-level
 * properties of an object without going deep into nested structures.
 * @param before The original object
 * @param after The modified object
 * @returns An array of changed property keys on the top level of the object.
 */
const getTopLevelDifferences = <T>(before: T, after: T): string[] => {
  // Handle null/undefined cases
  if (before == null && after == null) {
    // Both objects are null or undefined, no changes detected.
    return [];
  }
  if (before == null || after == null) {
    // If one is null/undefined and the other isn't, all properties are different
    return [...Object.keys(before || {}), ...Object.keys(after || {})];
  }

  // Use lodash to find differences between the objects
  const beforePairs = topairs(before);
  const afterPairs = topairs(after);

  // Find properties that are different between before and after
  const changedInBefore = differencewith(beforePairs, afterPairs, isequal);
  const changedInAfter = differencewith(afterPairs, beforePairs, isequal);

  // Get unique property keys that have changed
  const changedKeys = new Set([
    ...changedInBefore.map(([key]) => key),
    ...changedInAfter.map(([key]) => key),
  ]);

  return Array.from(changedKeys);
};

// This function should never be used directly, use `createAuditBundle` or `createAuditBundleWithVersions` instead
const createAuditEvent = (props: CreateAuditEvent) => {
  const {
    event,
    when,
    editor,
    toWhatResource,
    toWhatId,
    isFullUrl,
    changedProperties,
    previousVersion,
    createdVersion,
  } = props;

  const byWhat = createReferenceFromResource(editor);
  const toWhat = isFullUrl
    ? createUrlReference(toWhatId)
    : createReference(toWhatResource, toWhatId);

  const recorded = when instanceof Date ? when.toISOString() : when;

  // Create entity detail with previous or created version if available
  const entityDetail = [];
  if (previousVersion) {
    entityDetail.push({
      type: 'previousVersion',
      valueString: JSON.stringify(previousVersion),
    });
  }
  if (createdVersion) {
    entityDetail.push({
      type: 'createdVersion',
      valueString: JSON.stringify(createdVersion),
    });
  }
  if (changedProperties && changedProperties.length > 0) {
    entityDetail.push({
      type: 'changedProperties',
      valueString: changedProperties.join(', '),
    });
  }

  const auditEvent: AuditEvent = {
    resourceType: 'AuditEvent',
    recorded,
    type: {
      system,
      code: event,
    },
    agent: [
      {
        requestor: false,
        who: byWhat,
      },
    ],
    source: {
      observer: {
        identifier: {
          // this is the source of the event, the identifier is used for aggregating and analysis
          value: 'quro-app',
        },
      },
    },
    entity: [
      {
        what: toWhat,
        detail: entityDetail.length > 0 ? entityDetail : undefined,
      },
    ],
  };

  return auditEvent;
};

/**
 * Creates Audit events for the given fhir resources.
 * @param resources fhir resources that needs to be updated of created
 * @param byWhoId the user who has initiated the change
 * @param eventCode the audit event code to be used
 * @param entryMethod the bundle method to be used for the entry
 * @returns A mixed bundle of both the audit events and the given fhir resources.
 */
export const createAuditBundle = (
  resources: FhirResource[],
  editor: Editor,
  eventCode: AuditEventCode,
  entryMethod: BundleEntryRequest['method'] = 'POST',
): MixedBundle<AuditEvent | FhirResource> => {
  const resourceEntries = resources
    .map(mapResourceToBundleEntry(entryMethod))
    // add fullURL refs so that we can refer to not-yet-created resources
    .map(attachBundleFullUrl);

  const auditEntries = resourceEntries.map((o) => {
    const auditEvent = createAuditEvent({
      event: eventCode,
      toWhatId: (o.resource.id || o.fullUrl) as string,
      toWhatResource: o.resource.resourceType,
      when: new Date(),
      isFullUrl: !o.resource.id,
      editor,
      createdVersion: o.resource,
    });

    const auditEntry = createBundleEntry('POST', auditEvent);

    return auditEntry;
  });

  const bundle = createBundle<AuditEvent | FhirResource>([
    ...resourceEntries,
    ...auditEntries,
  ]);

  return bundle;
};

/**
 * Creates Audit events for the given fhir resources with support for previous and created versions.
 * @param resources fhir resources that needs to be updated of created
 * @param editor the user who has initiated the change
 * @param eventCode the audit event code to be used
 * @param entryMethod the bundle method to be used for the entry
 * @param resourceVersions For POST operations: provide created versions. For PUT operations: provide previous versions for change tracking.
 * @returns A mixed bundle of both the audit events and the given fhir resources.
 */
export const createAuditBundleWithVersions = (
  resources: FhirResource[],
  editor: Editor,
  eventCode: AuditEventCode,
  entryMethod: BundleEntryRequest['method'],
  resourceVersions?: FhirResource[],
): MixedBundle<AuditEvent | FhirResource> => {
  const resourceEntries = resources
    .map(mapResourceToBundleEntry(entryMethod))
    // add fullURL refs so that we can refer to not-yet-created resources
    .map(attachBundleFullUrl);

  const auditEntries = resourceEntries.map((o, index) => {
    const versionResource = resourceVersions?.[index];

    // For PUT operations, resourceVersions contains previous versions for change tracking
    // For POST operations, resourceVersions contains created versions for audit trail
    const isUpdateOperation = entryMethod === 'PUT';

    const changedProperties =
      isUpdateOperation && versionResource
        ? getTopLevelDifferences(versionResource, o.resource)
        : [];

    const auditEvent = createAuditEvent({
      event: eventCode,
      toWhatId: (o.resource.id || o.fullUrl) as string,
      toWhatResource: o.resource.resourceType,
      when: new Date(),
      isFullUrl: !o.resource.id,
      editor,
      changedProperties: changedProperties,
      previousVersion: isUpdateOperation ? versionResource : undefined,
      createdVersion: !isUpdateOperation ? versionResource : undefined,
    });

    const auditEntry = createBundleEntry('POST', auditEvent);

    return auditEntry;
  });

  const bundle = createBundle<AuditEvent | FhirResource>([
    ...resourceEntries,
    ...auditEntries,
  ]);

  return bundle;
};

/**
 * Creates an audit bundle for the supplied resources
 *
 * @param auditResources Resources that need to be updated or created
 * @param editor the user who has initiated the change
 * @param eventCode the audit event code to be used
 * @returns
 */
export const createAuditBundleWithMethods = <T extends FhirResource>(
  auditResources: AuditResource<T>[],
  editor: Editor,
  eventCode: AuditEventCode,
): MixedBundle<AuditEvent | T> => {
  const resourceEntries = auditResources.map(
    ({ method, resource, fullUrl }, key) => {
      const entry = mapResourceToBundleEntry(method, fullUrl)(resource);

      if (entry.fullUrl) {
        return entry;
      }

      return attachBundleFullUrl(entry, `__key_${key}`);
    },
  ) as BundleEntry<T>[];

  const auditEntries = resourceEntries.map((o) => {
    const auditEvent = createAuditEvent({
      editor,
      event: eventCode,
      toWhatId: (o.resource.id || o.fullUrl) as string,
      toWhatResource: o.resource.resourceType,
      when: new Date(),
      isFullUrl: !o.resource.id,
      createdVersion: o.resource,
    });

    const auditEntry = createBundleEntry('POST', auditEvent);

    return auditEntry;
  });

  const bundle = createBundle<AuditEvent | T>([
    ...resourceEntries,
    ...auditEntries,
  ]);

  return bundle;
};

type Method = BundleEntryRequest['method'];

/**
 * Create an audit resource that doesn't need to be referenced by any other resources - this does
 * not populate the `fullUrl` property
 */
export const createUnreferencedAuditResource = <T extends FhirResource>(
  method: Method,
  resource: T,
): AuditResource<T> => {
  return {
    method,
    resource,
  };
};

/**
 * Create a POST audit resource without a `fullUrl` field
 */
export const createUnreferencedAuditPOSTResource = <T extends FhirResource>(
  resource: T,
): AuditResource<T> => createUnreferencedAuditResource<T>('POST', resource);

export interface AuditResource<T extends FhirResource> {
  resource: T;
  method: Method;
  fullUrl?: string;
}

export type FullAuditResource<T extends FhirResource> = Required<
  AuditResource<T>
>;

/**
 * Create an Audit Resource which has a fullURL for referencing the resource from other items in
 * a bundle in the `urn:Resource:id` format
 */
export const createAuditResource = <T extends FhirResource>(
  resource: T,
  method: 'POST' | 'PUT',
  fullUrlKey: string,
): FullAuditResource<T> => ({
  resource,
  method,
  fullUrl: generateBundleReferenceId(resource.resourceType, fullUrlKey),
});

export const createAuditResourceReference = <T extends FhirResource>(
  auditResource: FullAuditResource<T>,
): CreateReference => ({
  reference: auditResource.fullUrl,
});
